import React, { useState } from 'react'
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom'
import { login } from '../api'
import { setSession, hasCachedSession } from '../session'
import { safeReturnPath } from '../hosts'
import Button from '../components/Button'
import Input from '../components/Input'
import { ErrorAlert } from '../components/Alert'
import { useDocumentTitle } from '../components/PublicLayout'
import AuthHeader from '../components/AuthHeader'

export default function SignIn() {
  useDocumentTitle('Sign in')
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Where RequireAuth was sending this visitor before it asked them to sign in
  // (#210). safeReturnPath is the only way this value reaches a navigation:
  // anything that is not an app page comes back as the dashboard.
  const returnTo = safeReturnPath(location.state?.from)

  // Already signed in: the marketing nav can no longer tell (the session is on
  // another host), so it always says "Sign in". Go straight on. A stale cached
  // session costs one bounce: the next page's 401 clears it and returns here.
  if (hasCachedSession()) return <Navigate to={returnTo} replace />

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await login(email, password)
      setSession(data)
      navigate(returnTo, { replace: true })
    } catch (err) {
      setError(err.message || 'Could not sign in. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <AuthHeader subtitle="Sign in to your account" />

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <ErrorAlert message={error} />
            <Input
              label="Email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="director@nonprofit.org"
            />
            <Input
              label="Password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
            />
            <Button type="submit" loading={loading} className="w-full">
              Sign In
            </Button>
          </form>

          <p className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-brand-500 hover:underline">
              Forgot your password?
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          Don't have an account?{' '}
          <Link to="/signup" className="text-brand-500 hover:underline">Start a free trial</Link>
        </p>
      </div>
    </div>
  )
}
